import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Hong Kong districts organized by region
const DISTRICTS_BY_REGION = {
  'Hong Kong Island': [
    'Aberdeen / Ap Lei Chau', 'Causeway Bay', 'Central', 'Chai Wan',
    'Chung Hom Kok', 'Deep Water Bay', 'Fortress Hill', 'Happy Valley',
    'Jardine\'s Lookout', 'Mid-levels Central', 'Mid-levels East',
    'Mid-levels West', 'North Point', 'North Point Hill', 'Pokfulam',
    'Quarry Bay', 'Repulse Bay', 'Sai Ying Pun', 'Shau Kei Wan',
    'Shek O', 'Sheung Wan', 'Shouson Hill', 'South Bay', 'Stanley',
    'Tai Hang', 'Tai Tam', 'The Peak', 'Tin Hau', 'Wan Chai',
    'Western / Kennedy Town', 'Wong Chuk Hang'
  ],
  'Kowloon': [
    'Cheung Sha Wan', 'Diamond Hill', 'Ho Man Tin', 'Hung Hom',
    'Jordan', 'Kai Tak', 'Kowloon Bay', 'Kowloon City',
    'Kowloon Station', 'Kowloon Tong', 'Kwun Tong', 'Lai Chi Kok',
    'Lam Tin', 'Mong Kok', 'Olympic Station', 'Sham Shui Po',
    'Shek Kip Mei', 'To Kwa Wan', 'Tsim Sha Tsui', 'Wong Tai Sin',
    'Yau Ma Tei', 'Yau Tong', 'Yau Yat Chuen'
  ],
  'New Territories': [
    'Clearwater Bay', 'Fanling', 'Kwai Chung', 'Ma On Shan',
    'Sai Kung', 'Sham Tseng', 'Shatin', 'Sheung Shui',
    'Tai Po', 'Tseung Kwan O / Hang Hau', 'Tsing Yi', 'Tsuen Wan',
    'Tuen Mun', 'Yuen Long'
  ],
  'Outlying Islands': [
    'Cheung Chau', 'Discovery Bay', 'Lamma Island', 'Lantau Island',
    'Ma Wan', 'Peng Chau', 'Tung Chung'
  ]
};

const HongKongDistrictSelector = ({
  selectedDistricts = [],
  onChange,
  className = ''
}) => {
  const [selectedIds, setSelectedIds] = useState(selectedDistricts);
  const [expandedRegions, setExpandedRegions] = useState({
    'Hong Kong Island': true,
    'Kowloon': false,
    'New Territories': false,
    'Outlying Islands': false
  });

  // Update parent component when selections change
  useEffect(() => {
    if (onChange) {
      onChange(selectedIds);
    }
  }, [selectedIds, onChange]);

  // When selectedDistricts prop changes, update local state
  useEffect(() => {
    setSelectedIds(selectedDistricts);
  }, [selectedDistricts]);

  // Toggle district selection
  const toggleDistrict = (district) => {
    setSelectedIds(prev => {
      if (prev.includes(district)) {
        return prev.filter(d => d !== district);
      } else {
        return [...prev, district];
      }
    });
  };

  // Toggle region expansion
  const toggleRegion = (region) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };

  // Select all districts in a region
  const selectAllInRegion = (region) => {
    const regionDistricts = DISTRICTS_BY_REGION[region];
    const allSelected = regionDistricts.every(d => selectedIds.includes(d));
    
    if (allSelected) {
      // Deselect all in this region
      setSelectedIds(prev => prev.filter(d => !regionDistricts.includes(d)));
    } else {
      // Select all in this region
      setSelectedIds(prev => [...new Set([...prev, ...regionDistricts])]);
    }
  };

  // Clear all districts in a region
  const clearRegion = (region, e) => {
    e.stopPropagation();
    const regionDistricts = DISTRICTS_BY_REGION[region];
    setSelectedIds(prev => prev.filter(d => !regionDistricts.includes(d)));
  };

  // Count selected districts in a region
  const getSelectedCount = (region) => {
    const regionDistricts = DISTRICTS_BY_REGION[region];
    return selectedIds.filter(d => regionDistricts.includes(d)).length;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {Object.entries(DISTRICTS_BY_REGION).map(([region, districts]) => {
        const selectedCount = getSelectedCount(region);
        const isExpanded = expandedRegions[region];
        
        return (
          <div key={region} className="border border-gray-600 rounded-lg overflow-hidden">
            {/* Region Header */}
            <div
              className="bg-gradient-to-r from-gray-700 to-gray-600 px-4 py-3 flex items-center justify-between cursor-pointer hover:from-gray-600 hover:to-gray-500 transition-colors"
              onClick={() => toggleRegion(region)}
            >
              <div className="flex items-center">
                {isExpanded ? (
                  <ChevronUp size={16} className="text-gray-300 mr-2" />
                ) : (
                  <ChevronDown size={16} className="text-gray-300 mr-2" />
                )}
                <span className="text-sm font-medium text-white">{region}</span>
                <span className="ml-2 text-xs text-gray-300 bg-gray-800 px-2 py-0.5 rounded">
                  {selectedCount}
                </span>
              </div>
              <button
                type="button"
                className="text-xs text-purple-400 hover:text-purple-300"
                onClick={(e) => {
                  clearRegion(region, e);
                }}
              >
                Clear
              </button>
            </div>

            {/* District List */}
            {isExpanded && (
              <div className="p-4 bg-[rgba(255,255,255,0.02)]">
                {/* Select All Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    selectAllInRegion(region);
                  }}
                  className="mb-3 text-xs text-purple-400 hover:text-purple-300 underline"
                >
                  {districts.every(d => selectedIds.includes(d)) ? 'Deselect All' : 'Select All'}
                </button>
                
                {/* District Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {districts.map((district) => (
                    <button
                      key={district}
                      type="button"
                      onClick={(e) => {
                        toggleDistrict(district);
                      }}
                      className={`px-3 py-2 rounded-md text-sm text-left transition-colors ${
                        selectedIds.includes(district)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {district}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HongKongDistrictSelector;